// Feed feature API client.
// Calls GET /api/v1/feed with optional filter/sort/search params.
// Types mirror api/schemas/feed.py FeedListResponse / FeedPostItem.
import { apiClient } from "@/lib/api-client";

export type PostType = "project" | "post";

export interface FeedPostItem {
  id: string;
  type: PostType;
  author_id: string | null;
  division: string;
  title: string;
  excerpt: string;
  tags: string[];
  vote_count: number;
  /**
   * Number of comments on this card's discussion (comments on the post, or —
   * for a training companion card — comments on the training resource).
   * Rendered YouTube-style: shown only when > 0, hidden otherwise.
   */
  comment_count: number;
  pinned: boolean;
  created_at: string; // ISO 8601 datetime string
  /** True when the current user has upvoted this post (per-user flag). */
  voted?: boolean;
  /**
   * Editorial cover image — the unfurled OpenGraph image of the post's
   * link/repo, when available. Null/absent → the UI renders generated
   * CoverArt instead.
   */
  cover_image_url?: string | null;
  /**
   * Set when this post is the feed companion of a TrainingResource rather
   * than an author-written post. When present, opening this card should
   * navigate to the Training detail page (this id), not PostDetail.
   */
  training_resource_id?: string | null;
  /**
   * Popularity-ordered emoji reaction summary for the card. Read-only on the
   * feed — reacting happens only in the detail view — but shown so a card
   * reflects the conversation around it. Empty when nobody has reacted.
   */
  reactions?: ReactionSummary[];
}

export interface ReactionSummary {
  emoji: string;
  count: number;
  reacted_by_me: boolean;
}

export interface EmptyState {
  headline: string;
  body: string;
}

export interface FeedListResponse {
  items: FeedPostItem[];
  empty_state: EmptyState | null;
  total: number;
}

export interface FeedQueryParams {
  division?: string;
  sort?: string;
  tag?: string;
  q?: string;
}

export async function fetchFeed(params?: FeedQueryParams): Promise<FeedListResponse> {
  const search = new URLSearchParams();
  if (params?.division) search.set("division", params.division);
  if (params?.sort) search.set("sort", params.sort);
  if (params?.tag) search.set("tag", params.tag);
  if (params?.q) search.set("q", params.q);
  const qs = search.toString() ? `?${search.toString()}` : "";
  return apiClient<FeedListResponse>(`/api/v1/feed${qs}`);
}
